import { useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

import { Button } from '../common/Button';
import { EntityLinkPicker } from '../common/EntityLinkPicker';
import { findTarget } from '../Banners/deepLinkTargets';
import {
  PICKABLE_ENTITY_LABELS,
  type PickableEntityType,
} from '../../services/pickableEntities';
import {
  SHAREABLE_TARGET_KEYS,
  buildAppSchemeUrl,
  buildShareUrl,
  copyShareUrl,
  type ShareableTargetKey,
} from '../../services/shareLinks';
import { buildDeepLink } from '../Banners/deepLinkTargets';

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

interface Picked {
  id: string;
  label: string;
}

/**
 * Build a public https link (and its QR code) for one piece of app content,
 * for posting on social or printing.
 *
 * Every type here is a push/banner deep-link target, so the dropdown labels,
 * the picker tab and the route all come from `deepLinkTargets` — this screen
 * adds the type list, the origin and the QR, nothing else.
 */
export function ShareLinkGenerator() {
  const [targetKey, setTargetKey] = useState<ShareableTargetKey>('reference');
  const [picked, setPicked] = useState<Picked | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** The target's optional number param — What's New's days window. */
  const [param, setParam] = useState('');
  const qrRef = useRef<HTMLCanvasElement>(null);

  const target = findTarget(targetKey);
  const pickType = target?.pick as PickableEntityType | undefined;
  const typeNoun = pickType ? PICKABLE_ENTITY_LABELS[pickType].toLowerCase() : 'item';
  const numberParam = target?.optionalNumberParam;
  // A static route (What's New) needs nothing picked; an entity route does.
  const ready = !pickType || picked !== null;
  const paramError = numberParam
    ? buildDeepLink({ mode: targetKey, id: '', customUrl: '', param }).error
    : null;

  const url = useMemo(
    () =>
      ready && !paramError
        ? buildShareUrl(targetKey, picked?.id ?? '', picked?.label, param)
        : null,
    [targetKey, picked, param, ready, paramError],
  );
  const schemeUrl = useMemo(
    () =>
      ready && !paramError
        ? buildAppSchemeUrl(targetKey, picked?.id ?? '', picked?.label, param)
        : null,
    [targetKey, picked, param, ready, paramError],
  );

  const downloadQr = () => {
    const canvas = qrRef.current;
    if (!canvas || !url) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    const slug = picked ? picked.id : param.trim() ? `${param.trim()}d` : 'default';
    a.download = `watchkeeper-${targetKey}-${slug.replace(/[^\w-]+/g, '_')}.png`;
    a.click();
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Share Links</h1>
        <p className="text-gray-500 mt-1">
          Build a public link to a reference, brand, movement or other content
          for social posts, email or print. It opens in the app when installed
          and offers the App Store / Google Play when it is not.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-5 max-w-2xl">
        <div>
          <label className={labelClass} htmlFor="share-link-type">
            Content type
          </label>
          <select
            id="share-link-type"
            value={targetKey}
            onChange={(e) => {
              // The picked id belongs to the type being left.
              setTargetKey(e.target.value as ShareableTargetKey);
              setPicked(null);
              setParam('');
            }}
            className={inputClass}
          >
            {SHAREABLE_TARGET_KEYS.map((key) => (
              <option key={key} value={key}>
                {findTarget(key)?.label ?? key}
              </option>
            ))}
          </select>
          {target?.help && (
            <p className="text-xs text-gray-500 mt-1">{target.help}</p>
          )}
          {target?.requiresAppHint && (
            <p className="text-xs text-amber-700 mt-1">{target.requiresAppHint}</p>
          )}
          {target?.shareHint && (
            <p className="text-xs text-amber-700 mt-1">{target.shareHint}</p>
          )}
        </div>

        {numberParam && (
          <div>
            <label className={labelClass} htmlFor="share-link-param">
              {numberParam.label}
            </label>
            <input
              id="share-link-param"
              type="number"
              inputMode="numeric"
              min={numberParam.min}
              max={numberParam.max}
              step={1}
              value={param}
              onChange={(e) => setParam(e.target.value)}
              placeholder={numberParam.placeholder}
              className={`${inputClass} max-w-xs ${paramError ? 'border-red-400' : ''}`}
            />
            {paramError && <p className="text-xs text-red-600 mt-1">{paramError}</p>}
          </div>
        )}

        {pickType && (
        <div>
          <label className={labelClass}>{target?.label ?? 'Item'}</label>
          <div className="flex items-center gap-2">
            {picked ? (
              <span className="inline-flex items-center gap-2 min-w-0 rounded-full bg-gray-100 border border-gray-300 px-3 py-1 text-sm">
                <span className="font-medium text-gray-900 truncate">
                  {picked.label || picked.id}
                </span>
                {picked.label && picked.label !== picked.id && (
                  <span className="text-xs text-gray-500 font-mono truncate">
                    {picked.id}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-sm text-gray-500">No {typeNoun} chosen</span>
            )}
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="text-sm text-brand-600 hover:text-brand-700 underline shrink-0"
            >
              {picked ? 'Change' : 'Choose…'}
            </button>
          </div>
        </div>
        )}

        {url && (
          <div className="pt-4 border-t space-y-4">
            <div>
              <label className={labelClass} htmlFor="share-link-url">
                Link
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="share-link-url"
                  type="text"
                  readOnly
                  value={url}
                  onFocus={(e) => e.target.select()}
                  className={`${inputClass} font-mono bg-gray-50`}
                />
                <Button variant="primary" onClick={() => void copyShareUrl(url)}>
                  Copy
                </Button>
              </div>
            </div>

            {schemeUrl && (
              <div>
                <label className={labelClass} htmlFor="share-link-scheme">
                  App link (opens the app directly; no store fallback)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="share-link-scheme"
                    type="text"
                    readOnly
                    value={schemeUrl}
                    onFocus={(e) => e.target.select()}
                    className={`${inputClass} font-mono bg-gray-50`}
                  />
                  <Button variant="secondary" onClick={() => void copyShareUrl(schemeUrl)}>
                    Copy
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-end gap-4">
              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <QRCodeCanvas
                  ref={qrRef}
                  value={url}
                  size={192}
                  marginSize={2}
                  aria-label={`QR code for ${url}`}
                />
              </div>
              <Button variant="secondary" size="sm" onClick={downloadQr}>
                Download QR (PNG)
              </Button>
            </div>
          </div>
        )}
      </div>

      {pickType && (
        <EntityLinkPicker<PickableEntityType>
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          types={[pickType]}
          initialType={pickType}
          title={`Choose a ${typeNoun}`}
          onSelect={(selection) => {
            setPicked({ id: selection.id, label: selection.label });
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
